import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubmissions } from '../SubmissionsContext';
import { API } from '../api';

const CATEGORY_COLOURS = {
  'Birthday': { bg: '#fff3e0', color: '#e65100' },
  'Achievement': { bg: '#e8f5e9', color: '#2e7d32' },
  'Outing': { bg: '#e3f2fd', color: '#1565c0' },
  'Community': { bg: '#f3e5f5', color: '#6a1b9a' },
  'Home Care': { bg: '#fce4ec', color: '#c62828' },
  'Magic Moment': { bg: '#fffde7', color: '#f57f17' },
};

const s = {
  page: { padding: '16px 16px 24px', maxWidth: '640px', margin: '0 auto' },
  heading: { fontSize: '20px', fontWeight: '700', color: '#222', marginBottom: '4px' },
  sub: { fontSize: '13px', color: '#888', marginBottom: '20px' },

  statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' },
  statCard: {
    background: '#fff', borderRadius: '12px', padding: '16px 18px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer',
  },
  statIcon: { fontSize: '32px', lineHeight: 1 },
  statInfo: {},
  statValue: { fontSize: '26px', fontWeight: '700', lineHeight: 1 },
  statLabel: { fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: '3px' },

  sectionTitle: { fontSize: '12px', fontWeight: '700', color: '#DB3267', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '12px' },

  quickGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '24px' },
  quickBtn: {
    background: '#fff', borderRadius: '10px', padding: '14px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)', border: '1.5px solid #f0f0f0',
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px', cursor: 'pointer', textAlign: 'left',
  },
  quickIcon: { fontSize: '24px' },
  quickLabel: { fontSize: '13px', fontWeight: '600', color: '#222' },
  quickSub: { fontSize: '11px', color: '#aaa' },

  activityCard: { background: '#fff', borderRadius: '12px', padding: '16px 18px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  activityRow: { display: 'flex', alignItems: 'flex-start', gap: '12px', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f5f5f5' },
  activityRowLast: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
  activityDot: { width: '8px', height: '8px', borderRadius: '50%', marginTop: '5px', flexShrink: 0 },
  activityText: { fontSize: '13px', color: '#333', lineHeight: '1.5' },
  activityTime: { fontSize: '11px', color: '#aaa', marginTop: '2px' },

  catCard: { background: '#fff', borderRadius: '12px', padding: '16px 18px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  catRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  catLabel: { fontSize: '13px', fontWeight: '500', width: '110px', flexShrink: 0 },
  catBar: { flex: 1, height: '8px', background: '#f0f0f0', borderRadius: '8px', overflow: 'hidden' },
  catFill: { height: '100%', borderRadius: '8px' },
  catCount: { fontSize: '12px', fontWeight: '700', color: '#555', width: '20px', textAlign: 'right' },

  alertCard: {
    background: '#fff8e1', border: '1.5px solid #ffe082', borderRadius: '10px',
    padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#7b5800',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
};

function statusDot(status) {
  if (status === 'approved') return '#2e7d32';
  if (status === 'declined') return '#e53935';
  if (status === 'deletedByUser') return '#9e9e9e';
  return '#fb8c00';
}

export default function AdminDashboard() {
  const { submissions, winner } = useSubmissions();
  const navigate = useNavigate();

  const thisMonthStr = '2026-03';
  const all = submissions;
  const pending = all.filter(s => s.status === 'pending' && !s.deletedByUser);
  const approved = all.filter(s => s.status === 'approved');
  const deleted = all.filter(s => s.deletedByUser);
  const thisMonth = all.filter(s => s.submittedAt && s.submittedAt.startsWith(thisMonthStr));

  // Category breakdown
  const catCounts = {};
  all.forEach(s => { catCounts[s.category] = (catCounts[s.category] || 0) + 1; });
  const catEntries = Object.entries(catCounts).sort((a, b) => b[1] - a[1]);
  const maxCat = Math.max(...Object.values(catCounts), 1);

  // Recent activity (last 5)
  const recent = [...all].sort((a, b) => b.id - a.id).slice(0, 5);

  const stats = [
    { icon: '⏳', value: pending.length, label: 'Pending Review', color: '#fb8c00', path: '/admin', filter: 'pending' },
    { icon: '✅', value: approved.length, label: 'Approved', color: '#2e7d32', path: '/admin', filter: 'approved' },
    { icon: '📸', value: thisMonth.length, label: 'This Month', color: '#DB3267', path: '/thismonth' },
    { icon: '🗑️', value: deleted.length, label: 'Deleted by Staff', color: '#9e9e9e', path: '/admin', filter: 'deleted' },
  ];

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Admin Dashboard</h1>
      <p style={s.sub}>Care Moments overview — March 2026</p>

      {pending.length > 0 && (
        <div style={s.alertCard}>
          <span style={{ fontSize: '20px' }}>📋</span>
          <span>
            <strong>{pending.length} submission{pending.length !== 1 ? 's' : ''}</strong> waiting for review.
            {' '}<span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/admin')}>Review now →</span>
          </span>
        </div>
      )}

      {/* Stats */}
      <div style={s.statsGrid}>
        {stats.map(stat => (
          <div key={stat.label} style={s.statCard} onClick={() => navigate(stat.path)}>
            <div style={s.statIcon}>{stat.icon}</div>
            <div style={s.statInfo}>
              <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
              <div style={s.statLabel}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div style={s.sectionTitle}>Quick Actions</div>
      <div style={s.quickGrid}>
        <button style={s.quickBtn} onClick={() => navigate('/admin')}>
          <div style={s.quickIcon}>📋</div>
          <div style={s.quickLabel}>Review Queue</div>
          <div style={s.quickSub}>{pending.length} pending</div>
        </button>
        <button style={s.quickBtn} onClick={() => navigate('/thismonth')}>
          <div style={s.quickIcon}>🏆</div>
          <div style={s.quickLabel}>Monthly Winner</div>
          <div style={s.quickSub}>{winner ? 'Winner announced' : 'Not yet picked'}</div>
        </button>
        <button style={s.quickBtn} onClick={() => navigate('/feed')}>
          <div style={s.quickIcon}>🏠</div>
          <div style={s.quickLabel}>View Feed</div>
          <div style={s.quickSub}>{approved.length} moments shared</div>
        </button>
        <button style={s.quickBtn} onClick={() => navigate('/submit')}>
          <div style={s.quickIcon}>📸</div>
          <div style={s.quickLabel}>Submit Moment</div>
          <div style={s.quickSub}>Share something</div>
        </button>
        <a
          href={`${API}/submissions/export.csv`}
          download="care-moments.csv"
          style={{ ...s.quickBtn, textDecoration: 'none' }}
        >
          <div style={s.quickIcon}>📥</div>
          <div style={s.quickLabel}>Export CSV</div>
          <div style={s.quickSub}>All submissions</div>
        </a>
      </div>

      {/* Recent activity */}
      <div style={s.sectionTitle}>Recent Activity</div>
      <div style={s.activityCard}>
        {recent.map((sub, i) => (
          <div key={sub.id} style={i === recent.length - 1 ? s.activityRowLast : s.activityRow}>
            <div style={{ ...s.activityDot, background: statusDot(sub.deletedByUser ? 'deletedByUser' : sub.status) }} />
            <div>
              <div style={s.activityText}>
                <strong>{sub.submittedBy}</strong> submitted a moment about <strong>{sub.about}</strong>
                {sub.deletedByUser && <span style={{ color: '#9e9e9e' }}> — deleted by user</span>}
                {sub.status === 'approved' && <span style={{ color: '#2e7d32' }}> — approved</span>}
                {sub.status === 'declined' && <span style={{ color: '#e53935' }}> — declined</span>}
              </div>
              <div style={s.activityTime}>{sub.submittedAt} · {sub.category} · {sub.shift} shift</div>
            </div>
          </div>
        ))}
      </div>

      {/* Category breakdown */}
      <div style={s.sectionTitle}>Submissions by Category</div>
      <div style={s.catCard}>
        {catEntries.map(([cat, count]) => {
          const col = CATEGORY_COLOURS[cat] || { bg: '#f5f5f5', color: '#555' };
          return (
            <div key={cat} style={s.catRow}>
              <div style={s.catLabel}>{cat}</div>
              <div style={s.catBar}>
                <div style={{ ...s.catFill, width: `${(count / maxCat) * 100}%`, background: col.color }} />
              </div>
              <div style={s.catCount}>{count}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
